import React, { useState, useEffect, useMemo } from 'react';
import { ShieldCheck, Loader2, RotateCcw, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { MODULE_GROUPS, getDefaultModulesForRole } from '@/contexts/AuthContext';
import { asignarModulosUsuario } from '@/pages/parametrizacion/usuarios/Services/users.services';

/**
 * Permisos por módulo de un usuario concreto.
 *
 * Regla importante: si el usuario queda con al menos un módulo marcado, esa
 * lista REEMPLAZA por completo a los permisos de su rol. Por eso, cuando aún no
 * tiene personalización, el modal arranca precargado con los módulos que le da
 * su rol: así personalizar nunca parte de cero ni le quita accesos sin que el
 * administrador lo vea.
 *
 * Guardar sin ningún módulo marcado equivale a "volver a los permisos del rol".
 */
const AsignarModulosUsuarioModal = ({ isOpen, onClose, usuario, onSuccess }) => {
  const { toast } = useToast();
  const [seleccionados, setSeleccionados] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const yaPersonalizado = useMemo(
    () => Array.isArray(usuario?.modulosActivos) && usuario.modulosActivos.length > 0,
    [usuario?.modulosActivos],
  );

  const modulosDelRol = useMemo(
    () => getDefaultModulesForRole(usuario?.rol),
    [usuario?.rol],
  );

  useEffect(() => {
    if (isOpen && usuario) {
      setSeleccionados(
        yaPersonalizado ? [...usuario.modulosActivos] : [...modulosDelRol],
      );
    }
  }, [isOpen, usuario, yaPersonalizado, modulosDelRol]);

  const toggle = (key) => {
    setSeleccionados((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key],
    );
  };

  const toggleGrupo = (grupo) => {
    const keys = grupo.modules.map((m) => m.key);
    const todos = keys.every((k) => seleccionados.includes(k));
    setSeleccionados((prev) =>
      todos
        ? prev.filter((m) => !keys.includes(m))
        : [...new Set([...prev, ...keys])],
    );
  };

  const guardar = async (modulos) => {
    setIsSaving(true);
    try {
      await asignarModulosUsuario(usuario._id, modulos);
      toast({
        title: 'Permisos actualizados',
        description: modulos.length
          ? `${usuario.nombre} tiene ${modulos.length} módulo(s) habilitado(s).`
          : `${usuario.nombre} vuelve a los permisos de su rol.`,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      toast({
        title: 'Error',
        description: e.message || 'No se pudieron guardar los permisos.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Permisos por módulo — <span className="text-primary">{usuario?.nombre}</span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Rol actual: <span className="font-medium">{usuario?.rol || '—'}</span>.
            {yaPersonalizado
              ? ' Este usuario ya tiene permisos personalizados.'
              : ' Se precargaron los módulos que le da su rol.'}
          </p>
        </DialogHeader>

        <div className="px-6 py-3 shrink-0">
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Si guardas con al menos un módulo marcado, esta lista
              <strong> reemplaza por completo </strong>
              los permisos del rol. Guardar sin nada marcado devuelve al usuario
              a los permisos de su rol.
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-4 custom-scrollbar">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {MODULE_GROUPS.map((grupo) => {
              const keys = grupo.modules.map((m) => m.key);
              const todos = keys.every((k) => seleccionados.includes(k));
              return (
                <div key={grupo.id} className="rounded-md border border-border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">{grupo.label}</p>
                    <button
                      type="button"
                      onClick={() => toggleGrupo(grupo)}
                      className="text-xs text-primary hover:underline"
                    >
                      {todos ? 'Quitar todo' : 'Marcar todo'}
                    </button>
                  </div>
                  <ul className="space-y-1.5">
                    {grupo.modules.map((m) => (
                      <li key={m.key} className="flex items-center gap-2">
                        <Checkbox
                          id={`mod-${m.key}`}
                          checked={seleccionados.includes(m.key)}
                          onCheckedChange={() => toggle(m.key)}
                        />
                        <label
                          htmlFor={`mod-${m.key}`}
                          className="text-sm cursor-pointer select-none"
                        >
                          {m.label}
                          {modulosDelRol.includes(m.key) && (
                            <span className="ml-1.5 text-[10px] text-muted-foreground">
                              (del rol)
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => guardar([])}
              disabled={isSaving}
              title="Guarda una lista vacía: el usuario vuelve a los permisos de su rol"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restablecer al rol
            </Button>
            <span className="text-xs text-muted-foreground">
              {seleccionados.length} módulo(s) marcado(s)
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancelar
            </Button>
            <Button onClick={() => guardar(seleccionados)} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Guardar permisos
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AsignarModulosUsuarioModal;
